import React, { Component } from 'react';
import CashCountCalculator from './CashCountCalculator';

var config = process.env;

/**
 * La caja como PANTALLA, no como modal (docs/spec-caja.md, F3).
 *
 * Era un modal y el contenido no entraba: con el desglose, los lotes por lector, los comprobantes
 * y las observaciones, el cierre no es un diálogo de dos campos — es la tarea principal de la
 * pantalla. Así que `/caja` muestra la caja en modo APERTURA o CIERRE según cómo esté, y el
 * historial pasa a ser lo secundario.
 *
 * El esperado NO se muestra de entrada, a propósito: si el cajero lo ve antes de contar puede
 * tipear ese número en vez de contar el cajón. Aparece al intentar cerrar, y sólo si hay algo que
 * explicar — un descuadre o comprobantes que no coinciden.
 *
 * Las transferencias SÍ se muestran, una por una, para tildar las que tienen comprobante (control de
 * caja, pedido del dueño 2026-10-19). No es el esperado del cajón: una transferencia no se cuenta,
 * lo que se controla es que haya llegado el comprobante. No es obligatorio tildarlas todas: la que
 * queda sin tildar no frena el cierre y la concilia un admin desde el control de caja.
 */
class CajaScreen extends Component {

    state = {
        responsible: '',
        amount: '',
        batches: {},
        receipts: '',
        // Las transferencias de la caja, para tildarlas. `null` = no se pudieron traer (un backend
        // anterior al control de caja): se cae al conteo a mano de antes.
        transfers: null,
        ticked: {},
        observations: '',
        posDevices: [],
        enviando: false,
        error: null,
        // El desglose que devuelve el backend cuando el cierre no cuadra.
        expected: null,
        difference: null,
        receiptsMismatch: false,
        obsInvalid: false,
    };

    obsRef = React.createRef();

    componentDidMount() {
        this.cargarLectores();
        if (this.props.session) {
            this.cargarTransferencias();
        }
    }

    cargarTransferencias = () => {
        this.api('cashSession/transfers')
            .then((data) => {
                if (data && data.error === 0 && data.data && Array.isArray(data.data.items)) {
                    // Las que ya estaban tildadas siguen tildadas al actualizar la lista.
                    this.setState({ transfers: data.data.items });
                    return;
                }
                this.setState({ transfers: null });
            })
            .catch(() => this.setState({ transfers: null }));
    };

    tildar = (ref) => this.setState({ ticked: { ...this.state.ticked, [ref]: !this.state.ticked[ref] } });

    tildadas = () => (this.state.transfers || []).filter((t) => this.state.ticked[t.ref]).map((t) => t.ref);

    // 'YYYY-MM-DD HH:mm:ss' de pared → 'HH:mm'. Sin `new Date()`: la correría de zona.
    horaDePared = (pared) => (pared ? String(pared).slice(11, 16) : '');

    money = (n) => `$${Number(n || 0).toLocaleString('de-DE')}`;

    api = (ruta, opciones) => fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + ruta, {
        headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        ...opciones,
    }).then((r) => r.json());

    cargarLectores = () => {
        this.api('posDevices')
            .then((data) => {
                if (data && data.error === 0 && Array.isArray(data.data)) {
                    this.setState({ posDevices: data.data });
                }
            })
            // Sin lectores el cierre sigue andando: el arqueo del efectivo no puede depender de esto.
            .catch(() => {});
    };

    sanear = (valor) => String(valor).replace(/[^\d,]/g, '').replace(/,/g, '.');

    formato = (n) => {
        if (n === '' || n === undefined || n === null) return '';
        const num = parseFloat(String(n).replace(/\./g, ''));
        return isNaN(num) ? '' : `$${num.toLocaleString('de-DE')}`;
    };

    responsableFalta = () => this.state.responsible.trim().length < 2;

    /**
     * El desglose es del intento ANTERIOR: si se cambia una cifra, deja de aplicar.
     *
     * Sin esto, corregir el problema no alcanzaba — arreglabas la cantidad de comprobantes y la
     * pantalla te seguía exigiendo una explicación por un descuadre que ya no existía.
     */
    nuevoIntento = (cambio) => this.setState({
        ...cambio, expected: null, obsInvalid: false, error: null,
    });

    abrir = () => {
        if (this.responsableFalta() || this.state.enviando) return;
        this.setState({ enviando: true, error: null });

        this.api('cashSession/open', {
            method: 'POST',
            body: JSON.stringify({
                opened_by_name: this.state.responsible.trim(),
                opening_float: this.state.amount || 0,
            }),
        })
            .then((data) => {
                this.setState({ enviando: false });
                if (data && data.error === 0) {
                    this.props.onChange();
                    return;
                }
                this.setState({ error: (data && data.message) || 'No se pudo abrir la caja.' });
            })
            .catch(() => this.setState({ enviando: false, error: 'No se pudo abrir la caja.' }));
    };

    cerrar = () => {
        if (this.responsableFalta() || this.state.enviando) return;
        const conTildes = this.state.transfers !== null;
        if (this.state.amount === '' || (!conTildes && this.state.receipts === '')) {
            this.setState({ error: conTildes ? 'Falta el dinero contado.' : 'Falta el dinero contado o la cantidad de comprobantes.' });
            return;
        }
        // Ya sabemos que hay diferencia (el desglose está en pantalla). Sin observación el backend
        // va a devolver lo mismo, y desde afuera el botón parece no hacer nada: se avisa acá.
        if (this.state.expected && this.state.observations.trim() === '') {
            this.setState({ obsInvalid: true });
            if (this.obsRef.current) {
                this.obsRef.current.focus();
                this.obsRef.current.scrollIntoView({ block: 'center' });
            }
            return;
        }
        this.setState({ enviando: true, error: null });

        this.api('cashSession/close', {
            method: 'POST',
            body: JSON.stringify({
                closed_by_name: this.state.responsible.trim(),
                counted_cash: this.state.amount,
                // Con la lista: qué transferencias tienen comprobante. Sin ella (backend viejo): el
                // conteo a mano de siempre.
                ...(conTildes
                    ? { transfer_checks: this.tildadas() }
                    : { transfer_receipts: this.state.receipts }),
                batches: this.state.posDevices.map((d) => ({
                    pos_device_id: d.id,
                    declared_amount: this.state.batches[d.id] || 0,
                })),
                observations: this.state.observations.trim(),
            }),
        })
            .then((data) => {
                this.setState({ enviando: false });

                if (data && data.error === 0) {
                    this.props.onChange();
                    return;
                }

                // Recién acá aparece el desglose, y sólo porque hay algo que explicar.
                if (data && data.data && data.data.requiresReason) {
                    this.setState({
                        expected: data.data.expected,
                        difference: data.data.difference,
                        receiptsMismatch: !!data.data.receiptsMismatch,
                    });
                    return;
                }

                this.setState({ error: (data && data.message) || 'No se pudo cerrar la caja.' });
            })
            .catch(() => this.setState({ enviando: false, error: 'No se pudo cerrar la caja.' }));
    };

    renderDesglose = () => {
        const { expected, difference, receiptsMismatch, receipts } = this.state;
        if (!expected) return null;

        // Paleta explícita y no `alert-warning`: en este template esa clase es un naranja saturado
        // con el texto en claro encima, y acá el texto es LO que hay que leer para decidir si se
        // cierra. El aviso se marca con el borde, no tiñendo todo.
        const C = {
            borde: '#e6c47a', acento: '#c8901f', titulo: '#7a5a12',
            tenue: '#6b7680', fuerte: '#1b232c', malo: '#a8402f', sobra: '#8a6212',
            division: '#eceff1',
        };

        const linea = (label, valor, opciones = {}) => (
            <div
                className="d-flex justify-content-between"
                style={{
                    fontSize: 13.5,
                    padding: '3px 0',
                    fontWeight: opciones.fuerte ? 600 : 400,
                    color: opciones.color || (opciones.fuerte ? C.fuerte : C.tenue),
                }}
            >
                <span>{label}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: opciones.color || C.fuerte }}>
                    {opciones.crudo !== undefined ? opciones.crudo : this.money(valor)}
                </span>
            </div>
        );

        const titulo = (texto) => (
            <div style={{
                fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase',
                color: C.acento, fontWeight: 600, marginBottom: 6,
            }}>
                {texto}
            </div>
        );

        // Cada bloque es una columna angosta: con el ancho completo de la tarjeta, la etiqueta y su
        // número quedaban en extremos opuestos y se perdía cuál correspondía a cuál.
        const seccion = (contenido) => (
            <div style={{ maxWidth: 380 }}>{contenido}</div>
        );

        const dif = Number(difference);
        const hayDif = Math.abs(dif) >= 1;

        return (
            <div
                className="mt-3 mb-3"
                style={{
                    background: '#ffffff',
                    border: `1px solid ${C.borde}`,
                    borderLeft: `4px solid ${C.acento}`,
                    borderRadius: 6,
                    padding: '18px 20px',
                }}
            >
                <div style={{ fontSize: 15, fontWeight: 600, color: C.titulo, marginBottom: 14 }}>
                    Revisá antes de cerrar
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '18px 40px',
                }}>

                {seccion(
                    <>
                        {titulo('Efectivo')}
                        {linea('Fondo de apertura', expected.opening_float)}
                        {linea('Ventas en efectivo', expected.cash_sales)}
                        {expected.movements_in > 0 && linea('Ingresos', expected.movements_in)}
                        {expected.movements_out > 0 && linea('Retiros y gastos', -expected.movements_out)}
                        {linea('Debería haber', expected.expected_cash, { fuerte: true })}
                        {linea('Contaste', this.state.amount, { fuerte: true })}
                        {hayDif && linea(
                            dif > 0 ? 'Sobran' : 'Faltan',
                            Math.abs(dif),
                            { fuerte: true, color: dif > 0 ? C.sobra : C.malo }
                        )}
                    </>
                )}

                {this.state.transfers !== null && seccion(
                    <>
                        {titulo('Transferencias')}
                        {linea(
                            expected.transfers_count === 1 ? '1 transferencia' : `${expected.transfers_count} transferencias`,
                            expected.transfers
                        )}
                        {linea('Con comprobante', null, { crudo: `${this.tildadas().length} de ${expected.transfers_count}` })}
                        {this.tildadas().length < expected.transfers_count && (
                            <div style={{ fontSize: 12.5, color: C.tenue, paddingTop: 2 }}>
                                Las que quedan sin tildar las revisa un admin; no hace falta explicarlas para cerrar.
                            </div>
                        )}
                    </>
                )}

                {this.state.transfers === null && seccion(
                    <>
                        {titulo('Transferencias')}
                        {linea(
                            expected.transfers_count === 1 ? '1 transferencia' : `${expected.transfers_count} transferencias`,
                            expected.transfers
                        )}
                        {linea('Comprobantes que cargaste', null, {
                            crudo: String(receipts),
                            fuerte: receiptsMismatch,
                            color: receiptsMismatch ? C.malo : undefined,
                        })}
                        {receiptsMismatch && (() => {
                            const faltan = expected.transfers_count - Number(receipts);
                            const texto = faltan > 0
                                ? (faltan === 1 ? 'Falta 1 comprobante por mandar.' : `Faltan ${faltan} comprobantes por mandar.`)
                                : (faltan === -1 ? 'Cargaste 1 comprobante de más.' : `Cargaste ${-faltan} comprobantes de más.`);
                            return <div style={{ fontSize: 13, color: C.malo, paddingTop: 2 }}>{texto}</div>;
                        })()}
                    </>
                )}

                {seccion(
                    <>
                        {titulo('POS')}
                        {(expected.batches || []).map((b) => {
                            const declarado = Number(this.state.batches[b.pos_device_id]) || 0;
                            const descuadra = Math.abs(declarado - b.expected_amount) >= 1;
                            return (
                                <div
                                    className="d-flex justify-content-between"
                                    style={{ fontSize: 13.5, padding: '3px 0', color: C.tenue }}
                                    key={b.pos_device_id}
                                >
                                    <span>{b.name}</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums', color: C.fuerte }}>
                                        {this.money(b.expected_amount)}
                                        <span style={{ color: descuadra ? C.malo : C.tenue }}>
                                            {' · declaraste '}{this.money(declarado)}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                        {/* Las entradas de evento y las membresías se cobran sin elegir lector: esa
                            tarjeta está en alguno de los dos lotes, pero el sistema no sabe en cuál. */}
                        {expected.pos_unassigned > 0 && linea('Sin lector (eventos, membresías)', expected.pos_unassigned)}
                        {linea('Total tarjeta', expected.pos_total, { fuerte: true })}
                    </>
                )}

                {expected.mercadopago > 0 && seccion(
                    <>
                        {titulo('MercadoPago')}
                        {linea('Cobrado', expected.mercadopago)}
                    </>
                )}

                {/* Cuenta del equipo (spec-cuenta-empleados FR-054). El consumo a cuenta es
                    INFORMATIVO y no está sumado a ningún total: es plata que no entró. Está para
                    que quien cierra entienda por qué el POS vendió más de lo que hay en el cajón,
                    en vez de buscar un faltante que no existe.

                    Las cobranzas SÍ están dentro de los totales de arriba —entraron por el balde
                    de su medio— y se repiten acá sólo para decir de dónde salen. */}
                {(expected.employee_charges !== 0 || expected.employee_cash !== 0 || expected.employee_transfers !== 0) && seccion(
                    <>
                        {titulo('Cuenta del equipo')}
                        {expected.employee_charges !== 0 && linea('Consumo cargado a cuenta', expected.employee_charges)}
                        {expected.employee_charges !== 0 && (
                            <div style={{ fontSize: 12.5, color: C.tenue, paddingBottom: 4 }}>
                                No es plata: no entra a ningún total.
                            </div>
                        )}
                        {expected.employee_cash !== 0 && linea('Cobrado en efectivo', expected.employee_cash)}
                        {expected.employee_transfers !== 0 && linea('Cobrado por transferencia', expected.employee_transfers)}
                        {(expected.employee_cash !== 0 || expected.employee_transfers !== 0) && (
                            <div style={{ fontSize: 12.5, color: C.tenue, paddingTop: 2 }}>
                                Ya está sumado arriba, en el medio con el que pagaron.
                            </div>
                        )}
                    </>
                )}

                </div>
            </div>
        );
    };

    renderTransferencias = () => {
        const transfers = this.state.transfers || [];
        const tildadas = this.tildadas().length;
        return (
            <div className="form-group">
                <div className="d-flex justify-content-between align-items-baseline">
                    <label className="mb-1">Transferencias del turno</label>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={this.cargarTransferencias}>Actualizar</button>
                </div>
                {transfers.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: 14 }}>No hubo transferencias en esta caja.</div>
                ) : (
                    <>
                        <small className="text-muted d-block mb-2">
                            Tildá las que tienen el comprobante en el WhatsApp. No es obligatorio: las que queden
                            sin tildar las revisa un admin.
                        </small>
                        <div style={{ border: '1px solid #e1e5eb', borderRadius: 6, maxWidth: 560 }}>
                            {transfers.map((t, i) => (
                                <label
                                    key={t.ref}
                                    className="d-flex align-items-center mb-0"
                                    style={{
                                        gap: 10, padding: '7px 12px', cursor: 'pointer',
                                        borderTop: i === 0 ? 'none' : '1px solid #eceff1',
                                        background: this.state.ticked[t.ref] ? '#ecf6f1' : undefined,
                                    }}
                                >
                                    <input type="checkbox" checked={!!this.state.ticked[t.ref]} onChange={() => this.tildar(t.ref)} />
                                    <span style={{ width: 44, color: '#6b7680', fontVariantNumeric: 'tabular-nums' }}>{this.horaDePared(t.at)}</span>
                                    <span style={{ flex: 1 }}>
                                        {t.who || (t.source === 'cuenta_equipo' ? 'Cuenta del equipo' : 'Consumidor final')}
                                        <span style={{ color: '#6b7680', fontSize: 12.5 }}>
                                            {t.source === 'cuenta_equipo' ? ' · pago de la cuenta del equipo' : ` · venta #${t.purchaseId}`}
                                        </span>
                                    </span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{this.money(t.amount)}</span>
                                </label>
                            ))}
                        </div>
                        <small className="text-muted">{tildadas} de {transfers.length} con comprobante</small>
                    </>
                )}
            </div>
        );
    };

    campoResponsable = (isClosing) => (
        <div className="form-group">
            <label>{isClosing ? 'Responsable del cierre' : 'Responsable de apertura'}</label>
            <input
                type="text"
                className="form-control"
                value={this.state.responsible}
                onChange={(e) => this.setState({ responsible: e.target.value })}
                placeholder="Nombre y apellido"
                maxLength={60}
            />
            <small className="text-muted">
                Quién se hace cargo del cajón. Va aparte del usuario, porque la cuenta es compartida.
            </small>
        </div>
    );

    renderApertura = () => (
        <div className="card card-small mb-4">
            <div className="card-header border-bottom"><span>Abrir la caja</span></div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6">{this.campoResponsable(false)}</div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Dinero de apertura</label>
                            <input
                                type="text"
                                className="form-control"
                                value={this.formato(this.state.amount)}
                                onChange={(e) => this.setState({ amount: this.sanear(e.target.value) })}
                                placeholder="$0"
                                style={{ fontSize: '1.1rem' }}
                            />
                            {/* El fondo también se cuenta a mano, así que la calculadora sirve igual acá. */}
                            <CashCountCalculator onTotal={(total) => this.setState({ amount: total === 0 ? '' : String(total) })} />
                        </div>
                    </div>
                </div>

                {this.state.error && <div className="alert alert-danger py-2">{this.state.error}</div>}

                <button
                    className="btn btn-primary"
                    onClick={this.abrir}
                    disabled={this.state.enviando || this.responsableFalta()}
                >
                    Abrir caja
                </button>
            </div>
        </div>
    );

    renderCierre = () => (
        <div className="card card-small mb-4">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                <span>Cerrar la caja</span>
                <small className="text-muted">
                    Abrió {this.props.session.opened_by_name || '—'}
                </small>
            </div>
            <div className="card-body">
                {this.campoResponsable(true)}

                <div className="form-group">
                    <label>Contá el cajón y anotá cuánto hay</label>
                    <input
                        type="text"
                        className="form-control"
                        value={this.formato(this.state.amount)}
                        onChange={(e) => this.nuevoIntento({ amount: this.sanear(e.target.value) })}
                        placeholder="$0"
                        style={{ fontSize: '1.1rem' }}
                    />
                </div>

                <CashCountCalculator onTotal={(total) => this.nuevoIntento({ amount: total === 0 ? '' : String(total) })} />

                <div className="row">
                    {this.state.posDevices.map((d) => (
                        <div className="col-md-6 form-group" key={d.id}>
                            <label>Cierre de lote — {d.name}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={this.formato(this.state.batches[d.id] || '')}
                                onChange={(e) => this.nuevoIntento({
                                    batches: { ...this.state.batches, [d.id]: this.sanear(e.target.value) },
                                })}
                                placeholder="$0"
                            />
                        </div>
                    ))}
                </div>

                {this.state.transfers !== null ? this.renderTransferencias() : (
                <div className="form-group">
                    <label>Comprobantes de transferencia cargados</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        style={{ maxWidth: 160 }}
                        value={this.state.receipts}
                        onChange={(e) => this.nuevoIntento({ receipts: e.target.value.replace(/[^\d]/g, '') })}
                        placeholder="0"
                    />
                    <small className="text-muted">
                        Cuántos mandaste al WhatsApp. Tiene que haber uno por cada transferencia cobrada.
                    </small>
                </div>
                )}

                {this.renderDesglose()}

                {/* Las observaciones van pegadas al botón: cuando el cierre no cuadra, escribirlas
                    es el paso que falta para poder cerrar. Arriba de todo quedaban lejos del lugar
                    donde uno se entera de que hacen falta. */}
                <div className="form-group">
                    <label>
                        {this.state.expected ? 'Qué pasó' : 'Observaciones'}
                        {this.state.expected && <span style={{ color: '#a8402f' }}> · obligatorio</span>}
                    </label>
                    <textarea
                        ref={this.obsRef}
                        className={`form-control ${this.state.obsInvalid ? 'is-invalid' : ''}`}
                        rows="3"
                        value={this.state.observations}
                        onChange={(e) => this.setState({ observations: e.target.value, obsInvalid: false })}
                        placeholder={this.state.expected ? 'Contá qué pasó para poder cerrar igual' : 'Opcional'}
                    />
                    {this.state.obsInvalid && (
                        <div style={{ color: '#a8402f', fontSize: 13, marginTop: 4 }}>
                            Escribí qué pasó: sin eso la caja no se puede cerrar con diferencia.
                        </div>
                    )}
                </div>
                {this.state.error && <div className="alert alert-danger py-2">{this.state.error}</div>}

                <button
                    className={`btn ${this.state.expected ? 'btn-danger' : 'btn-primary'}`}
                    onClick={this.cerrar}
                    disabled={this.state.enviando || this.responsableFalta()}
                >
                    {this.state.expected ? 'Cerrar caja igual' : 'Cerrar caja'}
                </button>
            </div>
        </div>
    );

    render() {
        return this.props.session ? this.renderCierre() : this.renderApertura();
    }
}

export default CajaScreen;
